import type { GameConfig } from "../../shared/types";

// Two independent preset stores, both global to the browser (not scoped by
// lobby - presets are reusable templates a host brings to any lobby they
// create). "expert" is edited from the gear-icon Expert Settings modal;
// "lobby" is edited from the Lobby's own inline preset row. They're kept
// separate rather than sharing one list because they're surfaced in two very
// different contexts and a name saved from one modal showing up unexpectedly
// in the other would be confusing - even though both stores hold the same
// shape (a full `GameConfig` snapshot, since either surface's "Apply"/
// auto-apply always writes the whole config, not just the fields visible on
// that surface).
export type PresetScope = "expert" | "lobby";

const STORAGE_KEYS: Record<PresetScope, string> = {
  expert: "drawing-conclusions:presets",
  lobby: "drawing-conclusions:lobbyPresets",
};

function loadAll(scope: PresetScope): Record<string, GameConfig> {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[scope]);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveAll(scope: PresetScope, presets: Record<string, GameConfig>): void {
  try {
    localStorage.setItem(STORAGE_KEYS[scope], JSON.stringify(presets));
  } catch {
    // localStorage may be unavailable (private browsing quirks); presets are best-effort.
  }
}

export function listPresetNames(scope: PresetScope): string[] {
  if (scope === "lobby") ensureLobbyPresetsSeeded();
  return Object.keys(loadAll(scope)).sort((a, b) => a.localeCompare(b));
}

export function savePreset(scope: PresetScope, name: string, config: GameConfig): void {
  const presets = loadAll(scope);
  presets[name] = config;
  saveAll(scope, presets);
}

export function loadPreset(scope: PresetScope, name: string): GameConfig | null {
  if (scope === "lobby") ensureLobbyPresetsSeeded();
  return loadAll(scope)[name] ?? null;
}

// ---------------------------------------------------------------------------
// Built-in starting presets for the Lobby's preset picker
// ---------------------------------------------------------------------------

/** Mirrors `src/server/config/defaultConfig.ts` exactly - a fresh lobby's
 * starting config IS this preset, so keep the two in sync if either changes.
 * Duplicated here (rather than imported) because client code never imports
 * from `src/server/*` - the two bundles are built and shipped separately. */
const DEFAULT_PRESET_CONFIG: GameConfig = {
  roundsPerGame: 3,
  maxLobbySize: 12,
  playerListOrder: "turnOrder",
  scoresVisible: true,
  allowSpectators: true,
  achievementsEnabled: true,
  wordListId: "english",
  wordHintEnabled: true,
  timers: {
    wordSelectionSeconds: 30,
    drawingSeconds: 120,
    guessTimerFloorSeconds: 30,
    artistReconnectGraceSeconds: 30,
    roundStartDisplaySeconds: 3,
    artistSelectionDisplaySeconds: 2,
    scoreCalculationDisplaySeconds: 5,
    nextArtistDisplaySeconds: 2,
    victoryRevealDelaySeconds: 1,
    achievementStartDelaySeconds: 2,
    achievementPlayerRevealDelaySeconds: 4,
    achievementDotIntervalSeconds: 0.3,
    achievementNextDelaySeconds: 2,
    disconnectRemovalSeconds: 180,
  },
  scoring: {
    artistFirstGuess: 100,
    artistAdditionalGuess: 20,
    playerFirst: 100,
    playerSecond: 80,
    playerThird: 60,
    playerRemaining: 40,
    decayAmount: 5,
    decayIntervalSeconds: 5,
    redrawEnabled: true,
    redrawPenalty: 20,
    freeRedraws: 1,
    maxRedraws: 3,
  },
  difficultyMultipliers: { easy: 1.0, moderate: 1.5, difficult: 2.0 },
  achievements: {
    iGotYou: { enabled: true, points: 50 },
    picasso: { enabled: true, points: 50 },
    isThatABird: { enabled: true, points: 50 },
    consistency100: { enabled: true, points: 50 },
    worthATry: { enabled: true, points: 25 },
    perfection: { enabled: true, points: 100 },
    speedingTicket: { enabled: true, points: 25 },
    firstTry: { enabled: true, points: 25 },
    purity: { enabled: true, points: 50 },
    closeButNoCigar: { enabled: true, points: 25 },
    quietType: { enabled: true, points: 25 },
    photoFinish: { enabled: true, points: 50 },
  },
  chat: {
    rateLimitMessagesPerWindow: 7,
    rateLimitWindowMs: 1000,
    historyLimit: 200,
    guessCloseThreshold: 3,
    inactivityWipeMinutes: 10,
    wordFilter: { enabled: true, customWords: [] },
  },
  hints: { enabled: true, intervalSeconds: 30, maxRevealPercent: 20 },
  ccc: { schedule: "everyRound", allowDirectHighLevelPickup: false, debugMode: false, selectionSeconds: 60, disabledCards: [] },
};

/** Same baseline as Default, just longer/gentler for a relaxed group: a
 * longer drawing timer, achievements off, no Chaos Canvas Cards, and only
 * one (free) re-roll allowed. */
const CASUAL_GAME_PRESET_CONFIG: GameConfig = {
  ...DEFAULT_PRESET_CONFIG,
  achievementsEnabled: false,
  timers: { ...DEFAULT_PRESET_CONFIG.timers, drawingSeconds: 150 },
  scoring: { ...DEFAULT_PRESET_CONFIG.scoring, maxRedraws: 1 },
  ccc: { ...DEFAULT_PRESET_CONFIG.ccc, schedule: "off" },
};

const BUILTIN_LOBBY_PRESETS: Record<string, GameConfig> = {
  Default: DEFAULT_PRESET_CONFIG,
  "Casual Game": CASUAL_GAME_PRESET_CONFIG,
};

/** Seeds the two built-in presets into this browser's Lobby preset store the
 * very first time it's ever touched (the raw localStorage key is entirely
 * absent), so a fresh browser doesn't stare at an empty picker. Once seeded,
 * this never re-seeds from scratch - a host who deletes or overwrites
 * "Default"/"Casual Game" has that choice stick, this isn't a factory reset.
 * A browser that seeded before this function existed (or before some
 * GameConfig field existed) still gets its "Default"/"Casual Game" copies
 * backfilled with any newly-added fields every time - see
 * backfillBuiltinPresetFields(). */
function ensureLobbyPresetsSeeded(): void {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEYS.lobby);
  } catch {
    return;
  }
  if (raw === null) {
    saveAll("lobby", { ...BUILTIN_LOBBY_PRESETS });
    return;
  }
  backfillBuiltinPresetFields();
  migrateStaleModerateMultiplier();
}

const MODERATE_MIGRATION_KEY = "drawing-conclusions:migratedModerate150";
/** The factory `difficultyMultipliers.moderate` value before it was bumped
 * from 125% to 150% - see the constant below and CONFIGURATION.md. */
const OLD_DEFAULT_MODERATE = 1.25;

/** One-time fix for browsers that seeded "Default"/"Casual Game" back when the
 * factory moderate multiplier was 125% - backfillBuiltinPresetFields() above
 * deliberately never touches a key that's already present (so a host's real
 * customization sticks), which means those stale 1.25 copies would otherwise
 * never catch up to the new 1.5 default on their own. This corrects ONLY that
 * exact old value, only for these two known built-in preset names, and only
 * once per browser (tracked via MODERATE_MIGRATION_KEY) - a host who genuinely
 * set moderate to some other number is left untouched, same as any other
 * customization. Someone who deliberately chose exactly the old default is a
 * false positive we accept; the alternative (never correcting it) leaves every
 * already-seeded browser silently stuck on a number nobody actually chose. */
function migrateStaleModerateMultiplier(): void {
  try {
    if (localStorage.getItem(MODERATE_MIGRATION_KEY)) return;
  } catch {
    return;
  }
  const presets = loadAll("lobby");
  let changed = false;
  for (const name of Object.keys(BUILTIN_LOBBY_PRESETS)) {
    const existing = presets[name];
    if (existing?.difficultyMultipliers?.moderate === OLD_DEFAULT_MODERATE) {
      existing.difficultyMultipliers.moderate = BUILTIN_LOBBY_PRESETS[name]!.difficultyMultipliers.moderate;
      changed = true;
    }
  }
  if (changed) saveAll("lobby", presets);
  try {
    localStorage.setItem(MODERATE_MIGRATION_KEY, "1");
  } catch {
    // best-effort - worst case this re-checks (and no-ops once already fixed) next time
  }
}

/** A browser whose "Default"/"Casual Game" presets were seeded before some
 * GameConfig field existed (e.g. wordListId, or a nested field like one of the
 * achievements) has stored copies permanently missing that field - loading
 * that stale copy shows a blank input for it instead of the field's intended
 * value. Deep-fills only the genuinely missing keys (at any nesting depth)
 * from the current built-in definitions into these two well-known presets,
 * never touching a key that's already present - a host who deliberately
 * overwrote "Default" with their own values keeps them untouched. The server
 * has an equivalent fallback for the config itself (ConfigManager.replace()),
 * but that only helps once the preset is actually applied - this keeps the
 * preset picker's own stored data honest before that even happens. */
function backfillBuiltinPresetFields(): void {
  const presets = loadAll("lobby") as unknown as Record<string, Record<string, unknown>>;
  let changed = false;
  for (const name of Object.keys(BUILTIN_LOBBY_PRESETS)) {
    const existing = presets[name];
    if (!existing) continue;
    if (fillMissingFields(existing, BUILTIN_LOBBY_PRESETS[name] as unknown as Record<string, unknown>)) {
      changed = true;
    }
  }
  if (changed) saveAll("lobby", presets as unknown as Record<string, GameConfig>);
}

function fillMissingFields(target: Record<string, unknown>, canonical: Record<string, unknown>): boolean {
  let changed = false;
  for (const key of Object.keys(canonical)) {
    if (!(key in target)) {
      target[key] = canonical[key];
      changed = true;
    } else if (isPlainObject(canonical[key]) && isPlainObject(target[key])) {
      if (fillMissingFields(target[key] as Record<string, unknown>, canonical[key] as Record<string, unknown>)) {
        changed = true;
      }
    }
  }
  return changed;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
